import type {
  ImportInvoiceMutationOptions,
  ImportInvoicePayload,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import { importInvoice } from './importInvoice';

/**
 * Mutation hook for importing an invoice from staged files.
 *
 * Refetches every v2 invoice list on settle, on failure too: a request the
 * network dropped may still have created the invoice.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the import.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/import_invoice_invoices_import_post
 */
export function useMutationImportInvoice(
  options?: ImportInvoiceMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ImportInvoicePayload) => importInvoice(payload),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await queryClient.invalidateQueries({
        queryKey: queryKeysInvoice.getAllV2({}),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
